import { paginateSupabase } from '@/lib/supabase-paginate';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AccountBalanceInput {
  id: string;
  name: string;
  currency: string;
  balance: number | null;
}

export interface ConvertedBalance {
  accountId: string;
  accountName: string;
  originalCurrency: string;
  originalBalance: number | null;
  convertedBalance: number | null;
  rateUsed: number | null;
  ratePair: string | null;
  rateMethod: 'direct' | 'inverse' | 'etb-routed' | 'same' | null;
}

interface RateRow {
  currency_pair: string;
  rate: number;
  effective_date: string;
}

function getLatestRate(rates: RateRow[], pair: string): number | null {
  const match = rates
    .filter((r) => r.currency_pair === pair)
    .sort((a, b) => b.effective_date.localeCompare(a.effective_date));
  return match.length > 0 ? match[0].rate : null;
}

export function convertBalance(
  balance: number,
  fromCurrency: string,
  toCurrency: string,
  rates: RateRow[]
): { converted: number; rate: number; pair: string; method: ConvertedBalance['rateMethod'] } | null {
  if (fromCurrency === toCurrency) {
    return { converted: balance, rate: 1, pair: `${fromCurrency}/${toCurrency}`, method: 'same' };
  }

  // Direct: FROM/TO
  const directPair = `${fromCurrency}/${toCurrency}`;
  const directRate = getLatestRate(rates, directPair);
  if (directRate) {
    return { converted: balance * directRate, rate: directRate, pair: directPair, method: 'direct' };
  }

  // Inverse: TO/FROM → 1/rate
  const inversePair = `${toCurrency}/${fromCurrency}`;
  const inverseRate = getLatestRate(rates, inversePair);
  if (inverseRate && inverseRate !== 0) {
    const effectiveRate = 1 / inverseRate;
    return { converted: balance * effectiveRate, rate: effectiveRate, pair: `${inversePair} (inv)`, method: 'inverse' };
  }

  // Route through ETB
  if (fromCurrency !== 'ETB' && toCurrency !== 'ETB') {
    // fromCurrency → ETB
    let fromToEtb: number | null = null;
    const fromEtbDirect = getLatestRate(rates, `${fromCurrency}/ETB`);
    if (fromEtbDirect) {
      fromToEtb = fromEtbDirect;
    } else {
      const etbFromDirect = getLatestRate(rates, `ETB/${fromCurrency}`);
      if (etbFromDirect && etbFromDirect !== 0) fromToEtb = 1 / etbFromDirect;
    }
    // Also try: USD/ETB where fromCurrency=USD
    if (!fromToEtb) {
      const altPair = getLatestRate(rates, `${fromCurrency}/ETB`);
      if (altPair) fromToEtb = altPair;
    }

    // ETB → toCurrency
    let etbToTarget: number | null = null;
    const etbTargetDirect = getLatestRate(rates, `ETB/${toCurrency}`);
    if (etbTargetDirect) {
      etbToTarget = etbTargetDirect;
    } else {
      const targetEtbDirect = getLatestRate(rates, `${toCurrency}/ETB`);
      if (targetEtbDirect && targetEtbDirect !== 0) etbToTarget = 1 / targetEtbDirect;
    }

    if (fromToEtb && etbToTarget) {
      const effectiveRate = fromToEtb * etbToTarget;
      return {
        converted: balance * effectiveRate,
        rate: effectiveRate,
        pair: `${fromCurrency}→ETB→${toCurrency}`,
        method: 'etb-routed',
      };
    }
  }

  return null;
}

export function useConsolidatedBalances(
  accounts: AccountBalanceInput[],
  targetCurrency: string
) {
  const ratesQuery = useQuery({
    queryKey: ['exchange-rates-all-latest'],
    queryFn: async () => {
      const data = await paginateSupabase<RateRow>((from, to) =>
        supabase
          .from('exchange_rates')
          .select('currency_pair, rate, effective_date')
          .order('effective_date', { ascending: false })
          .range(from, to) as any
      );
      return data;
    },
  });

  const result = useMemo(() => {
    const rates = ratesQuery.data || [];

    const converted: ConvertedBalance[] = accounts.map((acc) => {
      if (acc.balance == null) {
        return {
          accountId: acc.id,
          accountName: acc.name,
          originalCurrency: acc.currency,
          originalBalance: null,
          convertedBalance: null,
          rateUsed: null,
          ratePair: null,
          rateMethod: null,
        };
      }

      const conv = convertBalance(acc.balance, acc.currency, targetCurrency, rates);

      return {
        accountId: acc.id,
        accountName: acc.name,
        originalCurrency: acc.currency,
        originalBalance: acc.balance,
        convertedBalance: conv?.converted ?? null,
        rateUsed: conv?.rate ?? null,
        ratePair: conv?.pair ?? null,
        rateMethod: conv?.method ?? null,
      };
    });

    const grandTotal = converted.reduce((sum, c) => {
      if (c.convertedBalance != null) return sum + c.convertedBalance;
      return sum;
    }, 0);

    const unconvertedCount = converted.filter(
      (c) => c.originalBalance != null && c.convertedBalance == null
    ).length;

    return { converted, grandTotal, unconvertedCount };
  }, [accounts, targetCurrency, ratesQuery.data]);

  return {
    ...result,
    isLoading: ratesQuery.isLoading,
    error: ratesQuery.error,
  };
}
